import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PlatformUserDataService } from '@data/_generated-api/platform-api/services/platform-user-data.service';
import { UpamErrorMapper } from '@data/_generated-api/error-mappers/upam-error-mapper';
import { SILENT_API_ERRORS } from '@data/_generated-api/http-interceptors/silent-api-errors';
import { AsyncResult } from '@common/result/asyncResult';
import { ok } from '@common/result/Result';
import { PlatformUserDataDto } from '@data/_generated-api/platform-api/models/platform-user-data-dto';
import { PlatformApiResultOfUserDataDto } from '@data/_generated-api/platform-api/models/platform-api-result-of-user-data-dto';

/**
 * Per-user opslag van app-instellingen en app-status (de `UserData`-rij van de
 * ingelogde gebruiker in de backend). Alle calls zijn stil (geen fout-toasts):
 * de sync draait op de achtergrond en de UI toont de status zelf in de
 * "Mijn instellingen"-dialoog.
 */
@Injectable({ providedIn: 'root' })
export class UserDataApiService {
  private readonly api = inject(PlatformUserDataService);
  private readonly errorMapper = inject(UpamErrorMapper);
  private readonly silentContext = new HttpContext().set(SILENT_API_ERRORS, true);

  async getMine(): AsyncResult<PlatformUserDataDto> {
    return this.toResult(await firstValueFrom(this.api.getUserdataMine(undefined, this.silentContext)));
  }

  async updateAppSettings(appSettingsJson: string): AsyncResult<PlatformUserDataDto> {
    return this.toResult(
      await firstValueFrom(
        this.api.postUserdataActionUpdateAppSettings({ body: { appSettingsJson } }, this.silentContext)
      )
    );
  }

  async updateAppState(appStateJson: string): AsyncResult<PlatformUserDataDto> {
    return this.toResult(
      await firstValueFrom(
        this.api.postUserdataActionUpdateAppState({ body: { appStateJson } }, this.silentContext)
      )
    );
  }

  private async toResult(result: PlatformApiResultOfUserDataDto | undefined): AsyncResult<PlatformUserDataDto> {
    // Bij offline/refused levert de error-interceptor een lege body op.
    if (!result?.isValid) {
      return this.errorMapper.mapInvalidResult(result?.errors);
    }
    return ok(result.value ?? {});
  }
}
